import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import {
    Button,
    Header,
    Link,
    Pagination,
    PropertyFilter,
    SpaceBetween
} from "@amzn/awsui-components-react";
import { Table } from "@amzn/awsui-components-react/polaris";
import { useCollection } from "@amzn/awsui-collection-hooks";
import { EmptyState, getMatchesCountText, PROPERTY_FILTERING_I18N_CONSTANTS } from "../../../phastos/table-config";
import { getReportingAggregatedDataForDST } from "../redux/exceptionAPIs";
import { isNull, isUndefined } from "lodash";
import { getCurrencyAmountForSummary } from "../common/adjustment/constants/errorSummaryUtils";
import { convertStringToPercentage } from "../../utils/utils";

// Constants moved outside component to prevent recreation on each render
const COLUMN_IDENTIFIER = {
    Month: 'month',
    LegalEntity: 'legalEntityName',
    TotalTransactionDSTScope: 'totalTransactionDSTScope',
    TotalTransaction: 'totalTransaction',
    NexusRatio: "nexusRatio",
    DSTRate: "dstRate",
    TotalRevenue: "totalRevenue",
    DSTBaseAmount: "dstBaseAmount",
    DSTLiability: "dstLiability",
    Category: "category",
    TotalRevenueAmountDstReportingCurrency: "entityMarketplaceTotalRevenueAmountDstReportingCurrency",
    DstBaseAmountDstReportingCurrency: "entityMarketplaceDstBaseAmountDstReportingCurrency",
    DstLiabilityDstReportingCurrency: "entityMarketplaceDstLiabilityDstReportingCurrency"
};

const INITIAL_PAGE_NUMBER = 1;

// Empty states moved outside component
const EMPTY_STATES = {
    noSummary: (
        <EmptyState
            title="No summary"
            subtitle="No summary to display."
        />
    ),
    noMatches: (
        <EmptyState
            title="No matches"
            subtitle="We can't find a match."
        />
    ),
    noMatchesFilter: (
        <EmptyState
            title="No matches"
        />
    )
};

// Filtering properties moved outside component
const FILTERING_PROPERTIES = [
    {
        propertyLabel: 'Month',
        key: 'month',
        groupValuesLabel: 'Month',
        operators: ['=', '!='],
    },
    {
        propertyLabel: 'Legal Entity',
        key: 'legalEntity',
        groupValuesLabel: 'Legal Entity',
        operators: ['=', '!='],
    },
    {
        propertyLabel: 'Category',
        key: 'category',
        groupValuesLabel: 'Category',
        operators: ['=', '!='],
    },
];

function DSTSummary(props) {
    const {
        parentWorkflowId,
        useCase,
        lastUpdatedOn,
        setLastUpdatedOn,
        setEntityFilter,
        setActiveTabId,
        entityBreakDownTabId
    } = props;

    // State management
    const [data, setData] = useState({});
    const [loading, setLoading] = useState(false);
    const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null);
    const [currentPageNumber, setCurrentPageNumber] = useState(INITIAL_PAGE_NUMBER);
    const [currentPageData, setCurrentPageData] = useState([]);
    const [openEnd, setOpenEnd] = useState(true);
    const [maxPageReached, setMaxPageReached] = useState(1); // Track the highest page we've successfully loaded
    
    // Ref to track initial load
    const hasInitiallyLoaded = useRef(false);

    const getPageData = useCallback((pageNumber, dataObj = data) => {
        if (!dataObj || !dataObj[pageNumber]) {
            return [];
        }
        const pageData = dataObj[pageNumber];
        return Array.isArray(pageData) ? pageData : Object.values(pageData);
    }, [data]);

    const resetPagination = () => {
        setCurrentPageNumber(INITIAL_PAGE_NUMBER);
        setMaxPageReached(1);
        setLastEvaluatedKey(null);
        setOpenEnd(true);
    };

    const refreshData = (pageNumber, isRefresh = false) => {
        if (loading) return; // Prevent multiple simultaneous calls
        
        setLoading(true);
        
        // For refresh, start with empty data and reset lastEvaluatedKey
        const dataToUse = isRefresh ? {} : data;
        const keyToUse = isRefresh ? null : lastEvaluatedKey;
        
        const request = {
            data: dataToUse,
            setData,
            setLastUpdatedOn,
            lastEvaluatedKey: keyToUse,
            setLastEvaluatedKey,
            parentWorkflowId,
            useCase,
            currentPageNumber: pageNumber,
            setOpenEnd,
            setLoading
        };
        
        try {
            getReportingAggregatedDataForDST(request);
        } catch (error) {
            console.error('Error fetching DST data:', error);
            setLoading(false);
        }
    };

    const onPaginationChange = (event) => {
        const pageNumber = event.detail.currentPageIndex;
        
        // Validate page number bounds
        if (pageNumber < 1) {
            return;
        }
        
        // If we know there's no more data and we're beyond the max page reached, don't proceed
        if (!openEnd && pageNumber > maxPageReached) {
            return;
        }
        
        setCurrentPageNumber(pageNumber);

        if (isNull(data[pageNumber]) || isUndefined(data[pageNumber])) {
            refreshData(pageNumber);
        } else {
            const pageData = getPageData(pageNumber, data);
            const uniqueEntities = deduplicatePageData(pageData);
            setCurrentPageData(uniqueEntities);
            setLoading(false);
        }
    };

    const onEntityClick = useCallback((entityName) => {
        setEntityFilter(entityName);
        setActiveTabId(entityBreakDownTabId);
    }, [setEntityFilter, setActiveTabId, entityBreakDownTabId]);

    const handleRefresh = () => {
        // Reset all pagination state
        resetPagination();
        setData({});
        setCurrentPageData([]);
        hasInitiallyLoaded.current = false;
        
        // Always refresh from page 1 with refresh flag
        refreshData(INITIAL_PAGE_NUMBER, true);
    };

    // Memoized column definitions to prevent recreation on each render
    const columnDefinitions = useMemo(() => [
        {
            id: COLUMN_IDENTIFIER.Month,
            cell: item => item.month,
            header: 'Month',
            sortingField: 'month',
            sortingComparator: (a, b) => {
                // Custom sorting for month strings in format "JAN_2025", "FEB_2025", etc.
                const parseMonth = (monthStr) => {
                    if (!monthStr) return new Date(0);
                    
                    const [monthName, year] = monthStr.split('_');
                    const monthMap = {
                        'JAN': 0, 'FEB': 1, 'MAR': 2, 'APR': 3, 'MAY': 4, 'JUN': 5,
                        'JUL': 6, 'AUG': 7, 'SEP': 8, 'OCT': 9, 'NOV': 10, 'DEC': 11
                    };
                    
                    const monthIndex = monthMap[monthName];
                    const yearNum = parseInt(year, 10);
                    
                    return new Date(yearNum, monthIndex, 1);
                };
                
                const dateA = parseMonth(a.month);
                const dateB = parseMonth(b.month);
                return dateA.getTime() - dateB.getTime();
            }
        },
        {
            id: COLUMN_IDENTIFIER.Category,
            cell: item => item.businessCategory,
            header: 'Category',
            sortingField: 'businessCategory'
        },
        {
            id: COLUMN_IDENTIFIER.LegalEntity,
            cell: item => (
                <Link onFollow={() => onEntityClick(item.legalEntity)}>
                    {item.legalEntity}
                </Link>
            ),
            header: 'Legal Entity',
            sortingField: 'legalEntity'
        },
        {
            id: COLUMN_IDENTIFIER.TotalTransactionDSTScope,
            cell: item => getCurrencyAmountForSummary(item.entityMarketplaceTotalDSTScopeTransaction),
            header: 'Total Transaction DST Scope',
            sortingField: 'entityMarketplaceTotalDSTScopeTransaction',
            sortingComparator: (a, b) => {
                const valueA = parseFloat(a.entityMarketplaceTotalDSTScopeTransaction || 0);
                const valueB = parseFloat(b.entityMarketplaceTotalDSTScopeTransaction || 0);
                return valueA - valueB;
            }
        },
        {
            id: COLUMN_IDENTIFIER.TotalTransaction,
            cell: item => getCurrencyAmountForSummary(item.entityMarketplaceTotalTransaction),
            header: 'Total Transaction',
            sortingField: 'entityMarketplaceTotalTransaction',
            sortingComparator: (a, b) => {
                const valueA = parseFloat(a.entityMarketplaceTotalTransaction || 0);
                const valueB = parseFloat(b.entityMarketplaceTotalTransaction || 0);
                return valueA - valueB;
            }
        },
        {
            id: COLUMN_IDENTIFIER.NexusRatio,
            cell: item => convertStringToPercentage(item.entityMarketplaceNexusRatio),
            header: 'Nexus Ratio',
            sortingField: 'entityMarketplaceNexusRatio',
            sortingComparator: (a, b) => {
                const valueA = parseFloat(a.entityMarketplaceNexusRatio || 0);
                const valueB = parseFloat(b.entityMarketplaceNexusRatio || 0);
                return valueA - valueB;
            }
        },
        {
            id: COLUMN_IDENTIFIER.DSTRate,
            cell: item => convertStringToPercentage(item.dstRate),
            header: 'DST Rate',
            sortingField: 'dstRate',
            sortingComparator: (a, b) => {
                const valueA = parseFloat(a.dstRate || 0);
                const valueB = parseFloat(b.dstRate || 0);
                return valueA - valueB;
            }
        },
        {
            id: COLUMN_IDENTIFIER.TotalRevenue,
            cell: item => getCurrencyAmountForSummary(item.entityMarketplaceTotalRevenue, item.localCurrency),
            header: 'Total Revenue (Local Currency)',
            sortingField: 'entityMarketplaceTotalRevenue',
            sortingComparator: (a, b) => {
                const valueA = parseFloat(a.entityMarketplaceTotalRevenue || 0);
                const valueB = parseFloat(b.entityMarketplaceTotalRevenue || 0);
                return valueA - valueB;
            }
        },
        {
            id: COLUMN_IDENTIFIER.DSTBaseAmount,
            cell: item => getCurrencyAmountForSummary(item.entityMarketplaceDstBaseAmount, item.localCurrency),
            header: 'DST Base Revenue (Local Currency)',
            sortingField: 'entityMarketplaceDstBaseAmount',
            sortingComparator: (a, b) => {
                const valueA = parseFloat(a.entityMarketplaceDstBaseAmount || 0);
                const valueB = parseFloat(b.entityMarketplaceDstBaseAmount || 0);
                return valueA - valueB;
            }
        },
        {
            id: COLUMN_IDENTIFIER.DSTLiability,
            cell: item => getCurrencyAmountForSummary(item.entityMarketplaceDstLiability, item.localCurrency),
            header: 'DST Liability (Local Currency)',
            sortingField: 'entityMarketplaceDstLiability',
            sortingComparator: (a, b) => {
                const valueA = parseFloat(a.entityMarketplaceDstLiability || 0);
                const valueB = parseFloat(b.entityMarketplaceDstLiability || 0);
                return valueA - valueB;
            }
        },
        {
            id: COLUMN_IDENTIFIER.TotalRevenueAmountDstReportingCurrency,
            cell: item => getCurrencyAmountForSummary(item.entityMarketplaceTotalRevenueAmountDstReportingCurrency, item.dstReportingCurrency),
            header: 'Total Revenue (DST Reporting Currency)',
            sortingField: 'entityMarketplaceTotalRevenueAmountDstReportingCurrency',
            sortingComparator: (a, b) => {
                const valueA = parseFloat(a.entityMarketplaceTotalRevenueAmountDstReportingCurrency || 0);
                const valueB = parseFloat(b.entityMarketplaceTotalRevenueAmountDstReportingCurrency || 0);
                return valueA - valueB;
            }
        },
        {
            id: COLUMN_IDENTIFIER.DstBaseAmountDstReportingCurrency,
            cell: item => getCurrencyAmountForSummary(item.entityMarketplaceDstBaseAmountDstReportingCurrency, item.dstReportingCurrency),
            header: 'DST Base Revenue (DST Reporting Currency)',
            sortingField: 'entityMarketplaceDstBaseAmountDstReportingCurrency',
            sortingComparator: (a, b) => {
                const valueA = parseFloat(a.entityMarketplaceDstBaseAmountDstReportingCurrency || 0);
                const valueB = parseFloat(b.entityMarketplaceDstBaseAmountDstReportingCurrency || 0);
                return valueA - valueB;
            }
        },
        {
            id: COLUMN_IDENTIFIER.DstLiabilityDstReportingCurrency,
            cell: item => getCurrencyAmountForSummary(item.entityMarketplaceDstLiabilityDstReportingCurrency, item.dstReportingCurrency),
            header: 'DST Liability (DST Reporting Currency)',
            sortingField: 'entityMarketplaceDstLiabilityDstReportingCurrency',
            sortingComparator: (a, b) => {
                const valueA = parseFloat(a.entityMarketplaceDstLiabilityDstReportingCurrency || 0);
                const valueB = parseFloat(b.entityMarketplaceDstLiabilityDstReportingCurrency || 0);
                return valueA - valueB;
            }
        },
    ], [onEntityClick]);

    // Effects
    useEffect(() => {
        if (!hasInitiallyLoaded.current) {
            hasInitiallyLoaded.current = true;
            refreshData(INITIAL_PAGE_NUMBER, true); // Initial load is like a refresh
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!isNull(data[currentPageNumber]) && !isUndefined(data[currentPageNumber])) {
            const pageData = getPageData(currentPageNumber, data);
            const uniqueEntities = deduplicatePageData(pageData);
            setCurrentPageData(uniqueEntities);
            setLoading(false);
            
            // Update max page reached - this tracks the highest page we've successfully loaded
            setMaxPageReached(prev => Math.max(prev, currentPageNumber));
        }
    }, [data, currentPageNumber, getPageData]);

    // Helper function to deduplicate current page data
    const deduplicatePageData = (pageData) => {
        if (!Array.isArray(pageData)) {
            return [];
        }
        
        const entityMap = new Map();
        
        for (const item of pageData) {
            if (item?.legalEntity) {
                const key = item.legalEntity;
                if (!entityMap.has(key)) {
                    entityMap.set(key, item);
                }
            }
        }
        
        return Array.from(entityMap.values());
    };

    // Memoized collection configuration
    const collectionConfig = useMemo(() => ({
        filtering: {
            empty: EMPTY_STATES.noSummary,
            noMatch: EMPTY_STATES.noMatches,
        },
        propertyFiltering: {
            filteringProperties: FILTERING_PROPERTIES,
            empty: <EmptyState title="No matches"/>,
            noMatch: EMPTY_STATES.noMatchesFilter,
        },
        sorting: {
            defaultState: {
                sortingColumn: {
                    sortingField: 'month'
                },
                isDescending: true
            }
        },
        selection: {}
    }), []);

    const {items, filteredItemsCount, collectionProps, propertyFilterProps} = useCollection(
        currentPageData,
        collectionConfig
    );

    return (
        <SpaceBetween size="xl">
            <Table
                {...collectionProps}
                loading={loading}
                sortingDisabled={false}
                header={
                    <div>
                        <Header
                            actions={<SpaceBetween size='xs' direction='horizontal'>
                                <Button onClick={handleRefresh}>Refresh</Button>
                            </SpaceBetween>}
                            description={`Last updated: ${lastUpdatedOn}`}
                        >
                            DST Summary
                        </Header>
                    </div>
                }
                columnDefinitions={columnDefinitions}
                resizableColumns={false}
                items={items}
                filter={
                    <PropertyFilter
                        i18nStrings={PROPERTY_FILTERING_I18N_CONSTANTS}
                        filteringProperties={propertyFilterProps.filteringProperties}
                        filteringOptions={propertyFilterProps.filteringOptions}
                        onChange={propertyFilterProps.onChange}
                        query={propertyFilterProps.query}
                        countText={getMatchesCountText(filteredItemsCount)}
                    />
                }
                pagination={<Pagination
                    currentPageIndex={currentPageNumber}
                    onChange={onPaginationChange}
                    pagesCount={openEnd ? undefined : maxPageReached}
                    openEnd={openEnd}
                    disabled={loading}
                />}
            />
        </SpaceBetween>
    )
}

export default DSTSummary;
